// =====================================================================
// Assessment Center — Student Panel
// Dashboard page — at-a-glance status only (no analytics/scoring,
// since grading isn't implemented in this phase).
// =====================================================================
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, PageHeader, Spinner, statusTone } from "../../../shared/components/ui";
import { listAvailableExams, listMyAttempts, type AvailableExam, type MyAttempt } from "../lib/mock-api";

export default function DashboardPage() {
  const [available, setAvailable] = useState<AvailableExam[]>([]);
  const [attempts, setAttempts] = useState<MyAttempt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listAvailableExams(), listMyAttempts()]).then(([a, m]) => {
      setAvailable(a);
      setAttempts(m);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-20 text-slate-500">
        <Spinner /> Loading dashboard…
      </div>
    );
  }

  const inProgress = attempts.filter((a) => a.status === "in_progress");
  const submitted = attempts.filter((a) => a.status === "submitted" || a.status === "graded");

  const stats = [
    { label: "Available exams", value: available.length },
    { label: "In progress", value: inProgress.length },
    { label: "Submitted", value: submitted.length },
  ];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Your Assessment Center at a glance." />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {stats.map((s) => (
          <Card key={s.label} className="p-5">
            <p className="font-display text-3xl font-semibold text-navy-900">{s.value}</p>
            <p className="mt-1 text-sm text-slate-500">{s.label}</p>
          </Card>
        ))}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card>
          <div className="flex items-center justify-between border-b border-border-soft px-5 py-4">
            <h3 className="font-display text-lg font-semibold text-navy-900">Continue where you left off</h3>
            <Link to="/student/assessment-center/my-exams" className="text-sm text-navy-700 hover:underline">
              View all
            </Link>
          </div>
          {inProgress.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-500">Nothing in progress right now.</p>
          ) : (
            <ul className="divide-y divide-border-soft">
              {inProgress.map((a) => (
                <li key={a.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm font-medium text-navy-900">{a.exam_title}</p>
                    <p className="text-xs text-slate-400">{a.duration_minutes} min</p>
                  </div>
                  <Link to={`/student/assessment-center/exams/${a.id}`}>
                    <Button className="px-3 py-1.5 text-xs">Resume</Button>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between border-b border-border-soft px-5 py-4">
            <h3 className="font-display text-lg font-semibold text-navy-900">Available exams</h3>
            <Link to="/student/assessment-center/available-exams" className="text-sm text-navy-700 hover:underline">
              View all
            </Link>
          </div>
          {available.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-500">No exams open for you right now.</p>
          ) : (
            <ul className="divide-y divide-border-soft">
              {available.slice(0, 5).map((exam) => (
                <li key={exam.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm font-medium text-navy-900">{exam.title}</p>
                    <p className="text-xs text-slate-400">
                      {exam.question_count} questions · {exam.duration_minutes} min
                    </p>
                  </div>
                  <Badge tone={statusTone(exam.status)}>{exam.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
